/**
 * useProjectAccess — OT-0009 §4.3.
 *
 * `isMember` / `isLead` no son propiedades de un artifact: salen de
 * cruzar `project.lead_member_id` y la lista de members contra `me.id`.
 * Ese cruce se hace una vez por página (nunca por fila) y baja como
 * prop — mismo patrón que ya usaba `canAssignOwner` en `PhaseDrillIn`
 * antes de esta OT, ahora consolidado en un solo lugar.
 *
 * `resolveProjectAccess` es la parte pura (testeable sin React ni mocks
 * de `useMe`); el hook solo la alimenta con `me.id`.
 */

import type { Project } from '../api/projects'
import type { ProjectMember } from '../api/project_members'
import { useMe } from '../auth/useMe'
import type { ProjectMembershipContext } from '../lib/tiers'

export interface ProjectAccess extends ProjectMembershipContext {
  /** `true` si es el líder activo (`project.lead_member_id`). Solo
   *  puede ser `true` si `isMember` también lo es. */
  isLead: boolean
}

const NOT_A_MEMBER: ProjectAccess = { isMember: false, isLead: false, section: null }

export function resolveProjectAccess(
  meId: string | null | undefined,
  project: Pick<Project, 'lead_member_id'>,
  members: Pick<ProjectMember, 'id' | 'user_id' | 'section'>[],
): ProjectAccess {
  if (!meId) return NOT_A_MEMBER
  const myMembership = members.find((m) => m.user_id === meId)
  if (!myMembership) return NOT_A_MEMBER
  const isLead = project.lead_member_id === myMembership.id
  // OT-0058 §4.5 — la sección limita lo que produce en ESTE proyecto.
  return { isMember: true, isLead, section: myMembership.section }
}

export function useProjectAccess(
  project: Pick<Project, 'lead_member_id'>,
  members: Pick<ProjectMember, 'id' | 'user_id' | 'section'>[],
): ProjectAccess {
  const { me } = useMe()
  return resolveProjectAccess(me?.id, project, members)
}
